import React from 'react'
import { cn } from '../../../lib/utils'
import {
  ProfessionalSlideLayout,
  TitleSlideLayout,
  SectionDividerLayout,
  ContentSlideLayout,
  TwoColumnSlideLayout,
  ShowcaseSlideLayout,
  TeamSlideLayout,
} from '../layouts/ProfessionalSlideLayouts'
import { Card, CardContent } from '../../ui/Card'
import { IconSolid } from '../../ui/Icon'

/**
 * ENHANCED PROFESSIONAL SLIDE TEMPLATES
 * 
 * These templates provide ready-to-use, professional slide patterns
 * with consistent branding, typography, and visual hierarchy.
 * Use these templates for consistent, modern presentations.
 */

// ============================================
// ENHANCED TITLE SLIDE
// ============================================
export interface EnhancedTitleSlideProps {
  title: string
  subtitle?: string
  author?: string
  date?: string
  company?: string
  tags?: string[]
  variant?: 'primary' | 'secondary' | 'accent' | 'gradient'
  background?: 'primary' | 'secondary' | 'accent' | 'gradient' // Add backward compatibility
  footer?: React.ReactNode
}

export const EnhancedTitleSlide: React.FC<EnhancedTitleSlideProps> = ({
  title,
  subtitle,
  author,
  date,
  company,
  tags,
  variant = 'primary',
  background, // Support backward compatibility
  footer,
}) => {
  // Use background prop if provided, otherwise use variant
  const slideVariant = background || variant
  
  if (footer) {
    // If footer is provided, use ProfessionalSlideLayout with custom content
    return (
      <ProfessionalSlideLayout 
        variant={slideVariant as 'primary' | 'secondary' | 'accent' | 'light' | 'dark' | 'gradient'} 
        pattern="subtle-grid"
      >
        <div className="flex-1 flex flex-col justify-center items-center text-center">
          {/* Tags */}
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-3 mb-8 justify-center animate-fade-in">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full text-sm font-medium border border-white/30"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Main Title */}
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold mb-8 animate-fade-in delay-1 leading-tight max-w-6xl">
            {title}
          </h1>

          {/* Subtitle */}
          {subtitle && (
            <p className="text-2xl md:text-3xl lg:text-4xl mb-16 animate-fade-in delay-2 opacity-90 max-w-5xl leading-relaxed">
              {subtitle}
            </p>
          )}

          {/* Author & Company Info */}
          <div className="animate-fade-in delay-3 opacity-90 space-y-2">
            {author && (
              <p className="text-xl font-semibold">{author}</p>
            )}
            {company && (
              <p className="text-lg opacity-80">{company}</p>
            )}
            {date && (
              <p className="text-base opacity-70">{date}</p>
            )}
          </div>

          {/* Footer */}
          {footer && (
            <div className="mt-16 animate-fade-in delay-4 opacity-80">
              {footer}
            </div>
          )}
        </div>
      </ProfessionalSlideLayout>
    )
  }
  
  // If no footer, use the standard TitleSlideLayout
  return (
    <TitleSlideLayout
      title={title}
      subtitle={subtitle}
      author={author}
      date={date}
      company={company}
      tags={tags}
      variant={slideVariant as 'primary' | 'secondary' | 'accent' | 'gradient'}
    />
  )
}

// ============================================
// ENHANCED CONTENT SLIDE (Standard content with professional styling)
// ============================================
export interface EnhancedContentSlideProps {
  title: string
  subtitle?: string
  children: React.ReactNode
  variant?: 'light' | 'dark'
  background?: 'light' | 'dark' | 'primary' | 'secondary' // Add backward compatibility
  layout?: 'standard' | 'wide' | 'centered'
  showDivider?: boolean
}

export const EnhancedContentSlide: React.FC<EnhancedContentSlideProps> = ({
  title,
  subtitle,
  children,
  variant = 'light',
  background, // Support backward compatibility
  layout = 'standard',
  showDivider = true,
}) => {
  // Use background prop if provided, otherwise use variant
  const slideVariant = background || variant
  
  return (
    <ContentSlideLayout
      title={title}
      subtitle={subtitle}
      variant={slideVariant as 'light' | 'dark'}
      layout={layout}
      showDivider={showDivider}
    >
      {children}
    </ContentSlideLayout>
  )
}

// ============================================
// ENHANCED GRID SLIDE (Alias for EnhancedFeatureGridSlide)
// ============================================
export interface EnhancedGridSlideProps {
  title: string
  subtitle?: string
  items: React.ReactNode[]
  columns?: 2 | 3 | 4
  variant?: 'light' | 'dark'
  background?: 'light' | 'dark' | 'primary' | 'secondary' // Add backward compatibility
  footer?: React.ReactNode
}

export const EnhancedGridSlide: React.FC<EnhancedGridSlideProps> = ({
  title,
  subtitle,
  items,
  columns = 3,
  variant = 'light',
  background, // Support backward compatibility
  footer,
}) => {
  // Use background prop if provided, otherwise use variant
  const slideVariant = background || variant
  
  return (
    <ProfessionalSlideLayout variant={slideVariant as 'light' | 'dark' | 'primary' | 'secondary' | 'accent' | 'gradient'}>
      <div className="flex-1 flex flex-col justify-center">
        {/* Header */}
        <header className="mb-12 flex-shrink-0 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight text-primary-600 dark:text-primary-400">
            {title}
          </h1>
          {subtitle && (
            <h2 className="text-xl md:text-2xl opacity-80 leading-relaxed">
              {subtitle}
            </h2>
          )}
          <div className="mt-6 w-20 h-1 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full mx-auto"></div>
        </header>

        {/* Grid Content */}
        <main className="flex-1 flex flex-col justify-center overflow-hidden">
          <div className={cn(
            'grid gap-8 w-full max-w-6xl mx-auto',
            columns === 2 && 'grid-cols-1 lg:grid-cols-2',
            columns === 3 && 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
            columns === 4 && 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
          )}>
            {items.map((item, index) => (
              <div
                key={index}
                className={cn(
                  'animate-fade-in',
                  `delay-${index + 1}`
                )}
              >
                {item}
              </div>
            ))}
          </div>
          {footer && (
            <div className="mt-8 flex-shrink-0">
              {footer}
            </div>
          )}
        </main>
      </div>
    </ProfessionalSlideLayout>
  )
}

// ============================================
// ENHANCED AGENDA SLIDE
// ============================================
export interface EnhancedAgendaSlideProps {
  title?: string
  subtitle?: string
  items: Array<{
    number?: number
    icon?: React.ReactNode
    title: string
    description?: string
    duration?: string
    items?: string[]
  }>
  variant?: 'light' | 'dark'
}

export const EnhancedAgendaSlide: React.FC<EnhancedAgendaSlideProps> = ({
  title = "Agenda",
  subtitle,
  items,
  variant = 'light',
}) => {
  return (
    <ContentSlideLayout
      title={title}
      subtitle={subtitle}
      variant={variant}
    >
      <div className={cn(
        'grid gap-6 w-full',
        items.length <= 2 && 'grid-cols-1 max-w-3xl mx-auto',
        items.length === 3 && 'grid-cols-1 lg:grid-cols-3',
        items.length === 4 && 'grid-cols-2',
        items.length > 4 && 'grid-cols-2 lg:grid-cols-3'
      )}>
        {items.map((item, index) => (
          <Card
            key={index}
            variant="elevated"
            className={cn(
              'h-full animate-fade-in hover:shadow-xl transition-all duration-300',
              `delay-${index + 1}`,
              'border-l-4 border-l-primary-600'
            )}
          >
            <CardContent className="p-6">
              <div className="flex items-start gap-4 mb-4">
                {(item.icon || item.number) && (
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-primary-600 to-primary-700 text-white flex items-center justify-center font-bold text-lg shadow-lg">
                    {item.icon || item.number}
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                      {item.title}
                    </h3>
                    {item.duration && (
                      <span className="text-sm text-primary-600 dark:text-primary-400 font-medium ml-2 flex-shrink-0 bg-primary-50 dark:bg-primary-900/30 px-2 py-1 rounded-full">
                        {item.duration}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {item.description && (
                <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
                  {item.description}
                </p>
              )}
              {item.items && item.items.length > 0 && (
                <ul className="space-y-2 text-sm">
                  {item.items.map((subItem, subIndex) => (
                    <li key={subIndex} className="flex items-start gap-3">
                      <span className="text-primary-600 dark:text-primary-400 mt-1 flex-shrink-0">
                        <IconSolid name="check" size="sm" />
                      </span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {subItem}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </ContentSlideLayout>
  )
}

// ============================================
// ENHANCED FEATURE GRID SLIDE
// ============================================
export interface EnhancedFeatureGridSlideProps {
  title: string
  subtitle?: string
  features: Array<{
    icon?: React.ReactNode
    title: string
    description?: string
    items?: string[]
    highlight?: boolean
  }>
  columns?: 2 | 3 | 4
  variant?: 'light' | 'dark'
}

export const EnhancedFeatureGridSlide: React.FC<EnhancedFeatureGridSlideProps> = ({
  title,
  subtitle,
  features,
  columns = 3,
  variant = 'light',
}) => {
  const gridCols = {
    2: 'grid-cols-1 lg:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }

  return (
    <ContentSlideLayout
      title={title}
      subtitle={subtitle}
      variant={variant}
    >
      <div className={cn('grid gap-8 w-full', gridCols[columns])}>
        {features.map((feature, index) => (
          <Card
            key={index}
            variant={feature.highlight ? "elevated" : "default"}
            className={cn(
              'h-full animate-fade-in hover:shadow-xl transition-all duration-300 group',
              `delay-${index + 1}`,
              feature.highlight && 'ring-2 ring-primary-600 ring-opacity-50'
            )}
          >
            <CardContent className="p-6 h-full flex flex-col">
              {feature.icon && (
                <div className="mb-4 text-primary-600 dark:text-primary-400 group-hover:scale-110 transition-transform duration-300">
                  <div className="w-12 h-12 flex items-center justify-center">
                    {feature.icon}
                  </div>
                </div>
              )}
              <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                {feature.title}
              </h3>
              {feature.description && (
                <p className="text-gray-600 dark:text-gray-300 mb-4 flex-1 leading-relaxed">
                  {feature.description}
                </p>
              )}
              {feature.items && feature.items.length > 0 && (
                <ul className="space-y-2 text-sm mt-auto">
                  {feature.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-3">
                      <span className="text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0">
                        <IconSolid name="check" size="sm" />
                      </span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </ContentSlideLayout>
  )
}

// ============================================
// ENHANCED SECTION DIVIDER
// ============================================
export interface EnhancedSectionDividerSlideProps {
  title: string
  subtitle?: string
  sectionNumber?: number
  icon?: React.ReactNode
  variant?: 'primary' | 'secondary' | 'accent' | 'gradient'
}

export const EnhancedSectionDividerSlide: React.FC<EnhancedSectionDividerSlideProps> = ({
  title,
  subtitle,
  sectionNumber,
  icon,
  variant = 'secondary',
}) => {
  return (
    <SectionDividerLayout
      title={title}
      subtitle={subtitle}
      sectionNumber={sectionNumber}
      icon={icon}
      variant={variant}
    />
  )
}

// ============================================
// ENHANCED COMPARISON SLIDE
// ============================================
export interface EnhancedComparisonSlideProps {
  title: string
  subtitle?: string
  leftTitle: string
  rightTitle: string
  leftContent: React.ReactNode
  rightContent: React.ReactNode
  variant?: 'light' | 'dark'
}

export const EnhancedComparisonSlide: React.FC<EnhancedComparisonSlideProps> = ({
  title,
  subtitle,
  leftTitle,
  rightTitle,
  leftContent,
  rightContent,
  variant = 'light',
}) => {
  const leftSection = (
    <div className="h-full">
      <Card variant="default" className="h-full border-l-4 border-l-red-500">
        <CardContent className="p-6 h-full flex flex-col">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <IconSolid name="x" className="text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
              {leftTitle}
            </h3>
          </div>
          <div className="flex-1">
            {leftContent}
          </div>
        </CardContent>
      </Card>
    </div>
  )

  const rightSection = (
    <div className="h-full">
      <Card variant="elevated" className="h-full border-l-4 border-l-green-500 shadow-xl">
        <CardContent className="p-6 h-full flex flex-col">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <IconSolid name="check" className="text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
              {rightTitle}
            </h3>
          </div>
          <div className="flex-1">
            {rightContent}
          </div>
        </CardContent>
      </Card>
    </div>
  )

  return (
    <TwoColumnSlideLayout
      title={title}
      subtitle={subtitle}
      leftContent={leftSection}
      rightContent={rightSection}
      variant={variant}
      columnRatio="1:1"
      gap="lg"
    />
  )
}

// ============================================
// ENHANCED TEAM SLIDE
// ============================================
export interface EnhancedTeamSlideProps {
  title?: string
  subtitle?: string
  members: Array<{
    name: string
    role: string
    email?: string
    image?: string
    social?: Array<{ platform: string; url: string; icon?: React.ReactNode }>
  }>
  variant?: 'light' | 'primary'
}

export const EnhancedTeamSlide: React.FC<EnhancedTeamSlideProps> = ({
  title = "Unser Team",
  subtitle,
  members,
  variant = 'light',
}) => {
  return (
    <TeamSlideLayout
      title={title}
      subtitle={subtitle}
      members={members}
      variant={variant}
    />
  )
}

// ============================================
// ENHANCED IMAGE SHOWCASE SLIDE
// ============================================
export interface EnhancedImageShowcaseSlideProps {
  title?: string
  subtitle?: string
  image: string
  caption?: string
  variant?: 'dark' | 'primary'
  fullScreen?: boolean
}

export const EnhancedImageShowcaseSlide: React.FC<EnhancedImageShowcaseSlideProps> = ({
  title,
  subtitle,
  image,
  caption,
  variant = 'dark',
  fullScreen = false,
}) => {
  return (
    <ShowcaseSlideLayout
      title={title}
      subtitle={subtitle}
      variant={variant}
      fullScreen={fullScreen}
    >
      <div className="w-full h-full flex flex-col justify-center items-center">
        <div className={cn(
          'relative rounded-2xl overflow-hidden shadow-2xl',
          fullScreen ? 'w-full h-full' : 'max-w-5xl max-h-[70vh]'
        )}>
          <img
            src={image}
            alt={title || 'Showcase image'}
            className="w-full h-full object-contain"
          />
        </div>
        {caption && (
          <p className="mt-8 text-lg opacity-80 text-center max-w-3xl leading-relaxed">
            {caption}
          </p>
        )}
      </div>
    </ShowcaseSlideLayout>
  )
}